import React, { useEffect, useRef, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import api from "../api";
import { RouteItemData } from "../types";
import AddRoute from "./components/AddRoute";
import RouteList from "./components/RouteList";
import RouteMap from "./components/RouteMap";

const colorPalette = [
  "#e6194B",
  "#3cb44b",
  "#ffe119",
  "#0082c8",
  "#f58231",
  "#911eb4",
  "#46f0f0",
  "#f032e6",
  "#d2f53c",
  "#fabebe",
];

const MapPage: React.FC = () => {
  const token = localStorage.getItem("accessToken");

  const [routes, setRoutes] = useState<RouteItemData[]>([]);
  const [addRouteVisible, setAddRouteVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // 1) Fetch all routes from the backend on mount
  useEffect(() => {
    const fetchAllRoutes = async () => {
      setLoading(true);
      try {
        const { data: response } = await api.get("route/fetch-routes");
        console.log(response);

        const data = response.map((route: any, index: number) => ({
          ...route,
          color: colorPalette[index % colorPalette.length],
        }));
        setRoutes(data);
      } catch (error) {
        console.error("Failed to fetch routes:", error);
      } finally {
        setLoading(false);
        // Add slight delay before rendering map to ensure container is ready
        setTimeout(() => {
          setMapReady(true);
        }, 200);
      }
    };

    fetchAllRoutes();
  }, [token]);

  // 2) Handle showing/hiding the AddRoute modal
  const handleAddRouteClick = () => setAddRouteVisible(true);
  const handleAddRouteClose = () => setAddRouteVisible(false);

  // 3) Called when a user saves a route
  const handleSaveRoute = () => {
    // If your AddRoute component does the POST, you can refetch after saving
    // or push the new route into `routes` state if the server returns it
    setAddRouteVisible(false);
  };

  // 4) Handle window resize for the map
  useEffect(() => {
    const handleResize = () => {
      // Force update when window resizes to ensure map container dimensions are correct
      if (!loading && mapReady) {
        setMapReady(false);
        setTimeout(() => setMapReady(true), 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [loading, mapReady]);

  return (
    <div className="min-h-screen bg-gray-100 p-4 flex flex-col items-center mb-8 mt-2">
      <div className="bg-white rounded-lg shadow-md w-full max-w-7xl p-6 mb-6 h-[500px]">
        <div
          ref={mapContainerRef}
          className="w-full h-[445px]"
          style={{ position: 'relative' }}
        >
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <TailSpin
                height={80}
                width={80}
                color="#2563eb"
                strokeWidth={2}
                ariaLabel="loading-spinner"
                visible={true}
              />
            </div>
          ) : (
            <>
              {/* Only render the map when not loading and map is ready */}
              {!loading && mapReady && routes.length > 0 && (
                <RouteMap
                  routes={routes}
                />
              )}
              {!loading && routes.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-gray-500">
                  No routes available. Click the + button to add a route.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md w-full max-w-7xl p-6">
        <h4 className="text-left text-xl font-bold text-gray-800 mb-4">
          Route List
        </h4>
        {!loading && <RouteList routes={routes} />}
      </div>

      {/* Floating + Button (Add Route) */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={handleAddRouteClick}
          className="w-14 h-14 bg-blue-500 hover:bg-blue-700 text-white text-3xl font-bold rounded-full flex items-center justify-center shadow-lg transition-all border border-white"
        >
          +
        </button>
      </div>

      {/* Modal for adding a route */}
      <AddRoute
        isVisible={addRouteVisible}
        onClose={handleAddRouteClose}
        onSave={handleSaveRoute}
      />
    </div>
  );
};

export default MapPage;